import type { LeadTravel } from '@/types';

// =============================================================================
// One patient, one hotel, one set of nights — never entered twice.
//
// The trip form's early warning. It mirrors the server's HotelStayGuard rule for
// rule so the desk hears about a repeated stay while still choosing the hotel,
// not after pressing Save — but the server stays the authority: this only ever
// sees the trips the page has loaded.
// =============================================================================

export interface HotelStayCandidate {
  /** The trip being edited — never collides with itself. */
  id?: string | null;
  hotel_id?: string | null;
  hotel_name?: string | null;
  check_in_date?: string | null;
  check_out_date?: string | null;
  arrival_date?: string | null;
  departure_date?: string | null;
}

const DAY = 24 * 60 * 60 * 1000;

const day = (value?: string | null): number | null => {
  if (!value) return null;
  const time = Date.parse(`${value.slice(0, 10)}T00:00:00Z`);
  return Number.isNaN(time) ? null : time;
};

/**
 * Check-in → check-out, each falling back to the flight date beside it. One
 * known end reads as a single night from that end; a same-day or reversed
 * range still occupies the night it starts on.
 */
export const stayWindow = (stay: HotelStayCandidate): [number, number] | null => {
  let from = day(stay.check_in_date) ?? day(stay.arrival_date);
  let to = day(stay.check_out_date) ?? day(stay.departure_date);

  if (from === null && to === null) return null;

  from ??= (to as number) - DAY;
  to ??= from + DAY;
  if (to <= from) to = from + DAY;

  return [from, to];
};

const name = (stay: HotelStayCandidate) => (stay.hotel_name ?? '').trim().toLowerCase();

const sameHotel = (a: HotelStayCandidate, b: HotelStayCandidate) => {
  if (a.hotel_id && b.hotel_id) return a.hotel_id === b.hotel_id;
  return name(a) !== '' && name(a) === name(b);
};

const overlaps = (a: [number, number] | null, b: [number, number] | null) => {
  // An undated stay can only collide with another undated one.
  if (a === null || b === null) return a === null && b === null;
  return a[0] < b[1] && a[1] > b[0];
};

/** The patient's trip this stay repeats, if any. */
export const findHotelStayConflict = (
  candidate: HotelStayCandidate,
  trips: LeadTravel[],
): LeadTravel | null => {
  if (!candidate.hotel_id && name(candidate) === '') return null;

  const window = stayWindow(candidate);
  // ArtDent 261002 — a trip's further hotel stays are stays too.
  const stays = trips.flatMap((trip) => [trip, ...(trip.hotel_stays ?? [])]);

  return (
    stays.find(
      (trip) =>
        trip.id !== candidate.id &&
        (trip.hotel_id || name(trip) !== '') &&
        sameHotel(candidate, trip) &&
        overlaps(window, stayWindow(trip)),
    ) ?? null
  );
};
